import { themePrimaryButtonCss } from '@webbuilder/theme'
// Faithful subset of the editor's CanvasRenderer for the dashboard's WYSIWYG
// preview. Reuses @webbuilder/style-engine for style resolution (identical to the
// editor) and @webbuilder/schemas for icons. Drops editor-only concerns (CMS
// bindings, behavior runtimes, selection) that AI landing sections don't need.
import { RATING_DEFAULTS, getIconSvg, ratingIconsHtml } from '@webbuilder/schemas'
import { resolveStyles, stylesToRecord } from '@webbuilder/style-engine'
import type { StyleProperties } from '@webbuilder/style-engine'
import type { Breakpoint, ElementNode, ElementStyles, PageTheme, ProjectContent, ThemeRole, ThemeRoleElementProps, SectionBackgroundRole } from './types'
import { firstImageUrl, resolveBindingValue, resolveComponentInstances } from './projectContent'

const IMAGE_PLACEHOLDER_SRC = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500' viewBox='0 0 800 500'%3E%3Crect width='800' height='500' fill='%23F8FAFC'/%3E%3Crect x='1' y='1' width='798' height='498' fill='none' stroke='%23CBD5E1' stroke-width='2' stroke-dasharray='10 6'/%3E%3Cg transform='translate(360 175)'%3E%3Crect width='80' height='80' rx='14' fill='%23E2E8F0'/%3E%3Cpath d='M12 56L26 36L38 48L50 28L68 56Z' fill='%2394A3B8'/%3E%3Ccircle cx='58' cy='26' r='9' fill='%23FCD34D'/%3E%3C/g%3E%3C/svg%3E"

// ─── small helpers (copied from editor css-helpers / tokenStyleOwnership) ──────

function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, (l) => `-${l.toLowerCase()}`)
}
const DESIGN_VH = 9
function normalizeVhInValue(value: string): string {
  return value.replace(/(\d+\.?\d*)vh/gi, (_m, n: string) => `${Math.round(parseFloat(n) * DESIGN_VH)}px`)
}

const TYPOGRAPHY_RENDER_STRIP_KEYS = ['color', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontFeatureSettings', 'fontVariationSettings', 'textDecoration', 'textTransform', 'letterSpacing', 'lineHeight']
const BUTTON_TOKEN_STYLE_KEYS = ['background', 'backgroundColor', 'backgroundImage', 'color', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontFeatureSettings', 'fontVariationSettings', 'letterSpacing', 'lineHeight', 'textAlign', 'textDecoration', 'textTransform', 'border', 'borderWidth', 'borderStyle', 'borderColor', 'borderRadius', 'boxShadow', 'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']
function stripTokenOwnedStyles(styles: Record<string, string>, keys: readonly string[]): Record<string, string> {
  const next = { ...styles }
  for (const key of keys) delete next[key]
  return next
}

// ─── escape ────────────────────────────────────────────────────────────────────

function escapeAttr(v: string): string {
  return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
function escapeText(v: string): string {
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
function escapeTextWithBreaks(v: string): string {
  return escapeText(v).replace(/\n/g, '<br/>')
}

// ─── style record → css ─────────────────────────────────────────────────────────

function stylesToCss(styles: Record<string, string>): string {
  return Object.entries(styles)
    .filter(([, value]) => value !== '' && value != null)
    .map(([prop, value]) => `${camelToKebab(prop)}:${normalizeVhInValue(String(value)).replace(/[<>]/g, '')}`)
    .join(';')
}

function normalizePositionRecord(record: Record<string, string>): Record<string, string> {
  if (record.position !== 'sticky') return record
  return { ...record, top: record.top || '0px', zIndex: record.zIndex || '10' }
}

// ─── theme ──────────────────────────────────────────────────────────────────────

/**
 * Los colores del tema como variables CSS.
 *
 * Con los tokens personalizados además de los nueve fijos: un proyecto puede
 * definir los suyos, y sin emitirlos aquí cualquier elemento que los use se
 * queda con una variable que no existe — que es parte de por qué una copia se
 * veía con otros colores que el sitio.
 */
export function themeVarsToCss(theme?: PageTheme): string {
  if (!theme) return ''
  const custom = 'colorComponents' in theme && Array.isArray(theme.colorComponents)
    ? (theme.colorComponents as { cssVar?: string; value?: string }[])
      .filter((token) => token.cssVar && token.value)
      .map((token) => `${token.cssVar}:${token.value};`)
    : []
  return [
    ...Object.entries(theme.colors).map(([key, value]) => `--wb-${key}:${value};`),
    ...custom,
  ].join('')
}

function roleCss(theme: PageTheme | undefined, role: ThemeRole): string {
  const styles = theme?.roles?.[role]
  return styles ? stylesToCss(stylesToRecord(styles)) : ''
}

export function richContentCss(theme?: PageTheme, selector = '[data-content-mode="html"]'): string {
  const headingMargin = 'margin:0.9em 0 0.28em'
  const blockMargin = 'margin:0 0 0.55em'
  const joinCss = (...parts: string[]) => parts.filter((part) => part.trim()).join(';')
  return `
${selector} > :first-child { margin-top: 0; }
${selector} > :last-child { margin-bottom: 0; }
${selector} p { ${joinCss(roleCss(theme, 'paragraph'), blockMargin)}; }
${selector} h1 { ${joinCss(roleCss(theme, 'heading1'), headingMargin)}; }
${selector} h2 { ${joinCss(roleCss(theme, 'heading2'), headingMargin)}; }
${selector} h3 { ${joinCss(roleCss(theme, 'heading3'), headingMargin)}; }
${selector} h4 { ${joinCss(roleCss(theme, 'heading4'), headingMargin)}; }
${selector} h5 { ${joinCss(roleCss(theme, 'heading5'), headingMargin)}; }
${selector} h6 { ${joinCss(roleCss(theme, 'heading6'), headingMargin)}; }
${selector} small { ${roleCss(theme, 'eyebrow')} }
${selector} ul,
${selector} ol { margin: 0 0 0.65em 1.25em; padding: 0; }
${selector} li { margin: 0.2em 0; }
${selector} blockquote { margin: 0.85em 0; padding-left: 1em; border-left: 3px solid color-mix(in oklch, var(--wb-text) 16%, transparent); color: color-mix(in srgb, var(--wb-text) 68%, transparent); }
${selector} a { color: var(--wb-primary); text-decoration: underline; text-underline-offset: 0.14em; }
${selector} img,
${selector} iframe { max-width: 100%; border-radius: 8px; }
${selector} img { height: auto; display: block; margin: 1.25em 0; }
${selector} iframe { width: 100%; aspect-ratio: 16 / 9; height: auto; display: block; margin: 1.25em 0; }
${selector} hr { border: 0; border-top: 1px solid color-mix(in oklch, var(--wb-text) 14%, transparent); margin: 1.6em 0; }
${selector} pre { margin: 1em 0; padding: 0.9em 1.1em; border-radius: 10px; overflow-x: auto; background: color-mix(in oklch, var(--wb-text) 6%, transparent); }
${selector} code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.92em; }

/* La imagen con su propio ancho: lo elige quien escribe, bloque a bloque.
   Una foto de producto a media anchura y una portada a sangre son cosas
   distintas, y sin esto todas salían iguales. */
${selector} figure.cms-figure { margin: 1.35em 0; }
${selector} figure.cms-figure img { width: 100%; margin: 0; }
${selector} figure.cms-figure[data-size="small"] { max-width: 42%; }
${selector} figure.cms-figure[data-size="medium"] { max-width: 64%; }
${selector} figure.cms-figure[data-size="large"] { max-width: 84%; }
${selector} figure.cms-figure[data-size="full"] { max-width: 100%; }
/* Y el ancho a medida, que llega en su propio estilo: el atributo sólo dice que
   lo tiene, para que ninguna de las medidas de arriba se le ponga por delante. */
${selector} figure.cms-figure[data-size="custom"] { max-width: 100%; }
${selector} figure.cms-figure[data-align="center"] { margin-left: auto; margin-right: auto; }
${selector} figure.cms-figure[data-align="left"] { margin-left: 0; margin-right: auto; }
${selector} figure figcaption { margin-top: 0.5em; font-size: 0.86em; text-align: center; color: color-mix(in srgb, var(--wb-text) 62%, transparent); }

/* El destacado y el botón: dos bloques que no existen en HTML llano y que un
   texto de venta pide a gritos. Marcado corriente con una clase, para que se
   vean igual aquí, en el lienzo y publicados. */
${selector} .cms-callout {
  display: flex;
  gap: 0.7em;
  margin: 1.1em 0;
  padding: 0.9em 1.1em;
  border-radius: 10px;
  border-left: 3px solid var(--wb-primary);
  background: color-mix(in oklch, var(--wb-primary) 7%, transparent);
}
${selector} .cms-callout > .cms-callout-mark { flex: none; line-height: 1.5; }
${selector} .cms-callout .cms-callout-body > :last-child { margin-bottom: 0; }
${selector} .cms-cta { margin: 1.2em 0; }
${selector} .cms-cta a {
  display: inline-block;
  padding: 0.75em 1.4em;
  border-radius: 8px;
  background: var(--wb-primary);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
}
${themePrimaryButtonCss(theme, `${selector} .cms-cta a`)}
${selector} .cms-embed { position: relative; width: 100%; margin: 1.35em 0; border-radius: 10px; overflow: hidden; }
${selector} .cms-embed iframe { margin: 0; }
`
}

function sectionBackgroundCss(role: SectionBackgroundRole | undefined): string | null {
  if (role === 'custom') return null
  const resolved = role && role !== 'auto' ? role : 'background'
  switch (resolved) {
    case 'background': return 'var(--wb-background)'
    case 'surface': return 'var(--wb-backgroundSecondary)'
    case 'muted': return 'color-mix(in oklch, var(--wb-background) 86%, var(--wb-primary))'
    case 'primary': return 'var(--wb-primary)'
    case 'accent': return 'color-mix(in oklch, var(--wb-accent) 18%, var(--wb-background))'
    default: return null
  }
}

function hasExplicitSectionBackground(node: ElementNode, breakpoint: Breakpoint): boolean {
  const local = stylesToRecord(resolveStyles(node.styles, breakpoint))
  return Boolean(local.background || local.backgroundColor || node.styleRefs?.colors?.background || node.styleRefs?.colors?.backgroundColor)
}

function inferThemeRole(node: ElementNode, local: Record<string, string>): ThemeRole | undefined {
  if (node.sectionType?.startsWith('header') || node.schemaId?.startsWith('nb-header')) return 'navigation'
  if (node.sectionType?.startsWith('footer') || node.schemaId?.includes('footer')) return 'footer'
  if (node.type === 'section') return 'section'
  if (node.type === 'button') return 'buttonPrimary'
  if (node.type === 'link') return 'link'
  if (node.type === 'image') return 'image'
  if (node.type === 'icon') return 'icon'
  if (node.type === 'badge') return 'badge'
  if (node.type === 'rating') return 'rating'
  if (node.type === 'avatarGroup') return 'avatarGroup'
  if (node.type === 'stat') return 'stat'
  if (node.type === 'div') {
    const hasCard = local.borderRadius && local.padding && local.background
    return hasCard ? 'card' : undefined
  }
  if (node.type !== 'text' && node.type !== 'richHeading') return undefined
  const fontSize = Number.parseFloat(local.fontSize ?? '1')
  if (fontSize >= 3.2) return 'heading1'
  if (fontSize >= 2.1) return 'heading2'
  if (fontSize >= 1.35) return 'heading3'
  if (fontSize >= 1.08) return 'heading4'
  if (fontSize >= 0.92 && Number.parseFloat(local.fontWeight ?? '400') >= 700) return 'heading5'
  if (fontSize < 0.85 && Number.parseFloat(local.fontWeight ?? '400') >= 700) return 'heading6'
  return 'paragraph'
}

function colorTokenValue(theme: PageTheme | undefined, tokenId: string | undefined): string {
  if (!tokenId || !theme) return ''
  if (theme.colors && Object.prototype.hasOwnProperty.call(theme.colors, tokenId)) return `var(--wb-${tokenId})`
  /* Un token propio del proyecto. `themeVarsToCss` ya emite su variable, así que
     aquí sólo hay que apuntar a ella con el nombre que ella misma declara. */
  const custom = 'colorComponents' in theme && Array.isArray(theme.colorComponents)
    ? (theme.colorComponents as { id?: string; cssVar?: string }[]).find((token) => token.id === tokenId)
    : undefined
  return custom?.cssVar ? `var(${custom.cssVar})` : ''
}

function applyColorRefs(record: Record<string, string>, node: ElementNode, theme: PageTheme | undefined): Record<string, string> {
  const colorRefs = node.styleRefs?.colors ?? {}
  const next = { ...record }
  for (const [prop, tokenId] of Object.entries(colorRefs)) {
    const value = colorTokenValue(theme, tokenId)
    if (!value) continue
    if ((prop === 'background' || prop === 'backgroundColor') && next.backgroundImage) {
      next.backgroundColor = value
      delete next.background
      continue
    }
    next[prop] = value
  }
  return next
}

type ComponentToken = {
  id: string
  role?: ThemeRole
  styles?: StyleProperties
  breakpoints?: Partial<Record<Exclude<Breakpoint, 'desktop'>, StyleProperties>>
}

/**
 * Los estilos globales de botón, tarjeta y sección, como en el editor: sin
 * ellos un estilo de diseño o un look de botones elegido en el asistente no se
 * veía en la vista previa, que sólo leía los roles.
 */
function componentToken(theme: PageTheme | undefined, list: string, id: string | undefined): ComponentToken | undefined {
  if (!id || !theme) return undefined
  const items = (theme as unknown as Record<string, unknown>)[list]
  return Array.isArray(items) ? (items as ComponentToken[]).find((item) => item.id === id) : undefined
}

function resolveThemedStyles(node: ElementNode, breakpoint: Breakpoint, theme?: PageTheme): Record<string, string> {
  const local = stylesToRecord(resolveStyles(node.styles, breakpoint))
  const hasCardRef = Boolean(node.styleRefs && Object.prototype.hasOwnProperty.call(node.styleRefs, 'card'))
  const component = componentToken(theme, 'buttonComponents', node.styleRefs?.button)
    ?? componentToken(theme, 'cardComponents', hasCardRef ? node.styleRefs?.card : node.themeRole === 'card' ? 'card-default' : undefined)
    ?? componentToken(theme, 'sectionComponents', node.styleRefs?.section)
  if (component?.styles) {
    const bp = breakpoint !== 'desktop' ? component.breakpoints?.[breakpoint] : undefined
    const token = stylesToRecord({ ...component.styles, ...bp })
    const own = node.type === 'button' ? stripTokenOwnedStyles(local, BUTTON_TOKEN_STYLE_KEYS) : local
    // La tarjeta y la sección vuelven a poner su token encima, como el editor.
    const resolved = applyColorRefs(node.type === 'button' ? { ...token, ...own } : { ...own, ...token }, node, theme)
    if (node.visibility?.[breakpoint] === false) resolved.display = 'none'
    return resolved
  }
  const typographyComponent = node.styleRefs?.typography && theme && 'typographyComponents' in theme
    ? (theme as PageTheme & {
        typographyComponents?: Array<{
          id: string
          role?: ThemeRole
          styles: StyleProperties
          breakpoints?: Partial<Record<Exclude<Breakpoint, 'desktop'>, StyleProperties>>
        }>
      }).typographyComponents?.find((item) => item.id === node.styleRefs?.typography)
    : undefined
  const role = typographyComponent?.role ?? node.themeRole ?? inferThemeRole(node, local)
  const desktopRole = typographyComponent?.styles ?? (role && theme ? (theme.roles?.[role] as StyleProperties | undefined) : undefined)
  if (!desktopRole) {
    const resolved = applyColorRefs(local, node, theme)
    if (node.visibility?.[breakpoint] === false) resolved.display = 'none'
    return resolved
  }
  const bpOverride = typographyComponent && breakpoint !== 'desktop'
    ? typographyComponent.breakpoints?.[breakpoint]
    : role && theme && breakpoint !== 'desktop'
      ? theme.roleBreakpoints?.[breakpoint]?.[role]
      : undefined
  const effectiveRole = bpOverride ? { ...desktopRole, ...bpOverride } : desktopRole
  const themeRecord = stylesToRecord(effectiveRole)
  const isButton = node.type === 'button'
  const localRecord = isButton
    ? stripTokenOwnedStyles(local, BUTTON_TOKEN_STYLE_KEYS)
    : node.themeRole || typographyComponent
    ? stripTokenOwnedStyles(local, TYPOGRAPHY_RENDER_STRIP_KEYS)
    : local
  const resolved = applyColorRefs({ ...themeRecord, ...localRecord }, node, theme)
  if (node.visibility?.[breakpoint] === false) resolved.display = 'none'
  return resolved
}

function visualProp(node: ElementNode, roleProps: ThemeRoleElementProps | undefined, key: keyof ThemeRoleElementProps, fallback: number): number {
  return (node[key as keyof ElementNode] as number | undefined) ?? roleProps?.[key] ?? fallback
}

const INNER_LAYOUT_PROPS = new Set(['display', 'flexDirection', 'flexWrap', 'flexFlow', 'justifyContent', 'justifyItems', 'alignItems', 'alignContent', 'gap', 'rowGap', 'columnGap', 'gridTemplateColumns', 'gridTemplateRows', 'gridAutoRows', 'gridAutoColumns', 'gridAutoFlow', 'gridTemplate', 'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'])

function splitSectionRecords(node: ElementNode, breakpoint: Breakpoint, theme: PageTheme | undefined): { outer: Record<string, string>; inner: Record<string, string> } {
  const sectionRecord = normalizePositionRecord({ ...resolveThemedStyles(node, breakpoint, theme) })
  const background = sectionBackgroundCss(node.backgroundRole ?? 'auto')
  if (background && !hasExplicitSectionBackground(node, breakpoint)) sectionRecord.background = background
  const inner: Record<string, string> = { flex: '1', maxWidth: '80rem', width: '100%', boxSizing: 'border-box', alignSelf: 'center' }
  const outer: Record<string, string> = { display: 'flex', flexDirection: 'column', position: 'relative' }
  for (const [prop, value] of Object.entries(sectionRecord)) {
    if (INNER_LAYOUT_PROPS.has(prop)) inner[prop] = value
    else outer[prop] = value
  }
  return { outer, inner }
}

// ─── content accessors ───────────────────────────────────────────────────────────
// Un nodo enlazado lee su valor de `ProjectContent`; sin ese contenido se cae al
// literal que tuviera escrito, que es lo que pasaba antes con TODOS ellos.

function nodeContent(node: ElementNode, projectContent?: ProjectContent): string {
  const bound = resolveBindingValue(node, projectContent)
  /* Vacío cuenta como no enlazado: aquí no se estampa ningún aviso de «sin
     contenido» —eso es del editor—, porque esto imita al sitio publicado. */
  if (bound !== undefined && bound !== '') return bound
  return node.content ?? ''
}
function nodeSrc(node: ElementNode, projectContent?: ProjectContent): string {
  if (!node.contentBinding) return node.src ?? ''
  const bound = resolveBindingValue(node, projectContent)
  return bound?.trim() ? firstImageUrl(bound) : node.src ?? ''
}
function normalizeHref(value: string): string {
  const v = (value ?? '').trim()
  if (!v) return '#'
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(v)) return v
  return `https://${v}`
}
function boundHref(node: ElementNode, projectContent?: ProjectContent): string | undefined {
  /* Sólo los campos que son direcciones. Un enlace atado al título de una ficha
     no quiere el título por href. Misma regla que el editor. */
  return node.contentBinding?.field.toLowerCase().includes('url')
    ? resolveBindingValue(node, projectContent)
    : undefined
}
function nodeHref(node: ElementNode, projectContent?: ProjectContent): string {
  return normalizeHref(boundHref(node, projectContent) ?? node.href ?? node.interaction?.href ?? '')
}
function nodeHasHref(node: ElementNode, projectContent?: ProjectContent): boolean {
  return Boolean(boundHref(node, projectContent) || node.href || node.interaction?.href)
}
function a11y(node: ElementNode): string {
  const parts: string[] = []
  if (node.ariaLabel) parts.push(` aria-label="${escapeAttr(node.ariaLabel)}"`)
  return parts.join('')
}

// ─── node → html (export style, clean) ───────────────────────────────────────────

function nodeToHtml(node: ElementNode, breakpoint: Breakpoint, theme: PageTheme | undefined, rootIndex: number, brand?: { id: string; text: string }, projectContent?: ProjectContent): string {
  const allRecord = normalizePositionRecord(resolveThemedStyles(node, breakpoint, theme))
  const role = node.themeRole ?? inferThemeRole(node, allRecord)
  const roleProps = role ? theme?.roleProps?.[role] : undefined
  if (node.anchorId && !allRecord.scrollMarginTop) allRecord.scrollMarginTop = '88px'
  const anchorAttr = node.anchorId ? ` id="${escapeAttr(node.anchorId)}"` : ''
  const richAttr = node.contentMode === 'html' ? ' data-content-mode="html"' : ''
  const schemaAttr = node.schemaId ? ` data-wb-schema-id="${escapeAttr(node.schemaId)}"` : ''
  const css = stylesToCss(allRecord)
  const outerAttrs = `${anchorAttr}${richAttr}${schemaAttr}${css ? ` style="${css}"` : ''}`.trim()
  const children = node.children.map((c) => nodeToHtml(c, breakpoint, theme, rootIndex, brand, projectContent)).join('')
  const isBrand = brand && node.id === brand.id

  switch (node.type) {
    case 'section': {
      const cw = node.containerWidth ?? '100%'
      const sectionRecord = normalizePositionRecord({ ...allRecord })
      const background = sectionBackgroundCss(node.backgroundRole ?? 'auto')
      if (background && !hasExplicitSectionBackground(node, breakpoint)) sectionRecord.background = background
      if (cw === '100%') {
        const fullCss = stylesToCss(sectionRecord)
        return `<section${anchorAttr}${richAttr}${fullCss ? ` style="${fullCss}"` : ''}>${children}</section>`
      }
      const { outer, inner } = splitSectionRecords(node, breakpoint, theme)
      const sectionStyle = stylesToCss(outer) || 'display:flex;flex-direction:column;position:relative'
      return `<section${anchorAttr}${richAttr} style="${sectionStyle}"><div style="${stylesToCss(inner)}">${children}</div></section>`
    }
    case 'div':
      return `<div ${outerAttrs}>${children}</div>`
    case 'text':
      if (isBrand) return `<p ${outerAttrs} data-brand="1">${escapeText(brand?.text ?? '')}</p>`
      return `<p ${outerAttrs}>${escapeTextWithBreaks(nodeContent(node, projectContent))}</p>`
    case 'richHeading': {
      if (isBrand) return `<h1 ${outerAttrs} data-brand="1">${escapeText(brand?.text ?? '')}</h1>`
      const before = escapeText(nodeContent(node, projectContent))
      const accent = escapeText(node.accentContent ?? '')
      const after = escapeText(node.afterContent ?? '')
      const accentColor = escapeAttr(theme?.colors.accent ?? theme?.colors.primary ?? 'currentColor')
      const inner = `${before}${accent ? ` <span style="font-style:italic;color:${accentColor}">${accent}</span>` : ''}${after ? ` ${after}` : ''}`
      return `<h1 ${outerAttrs}${a11y(node)}>${inner}</h1>`
    }
    case 'icon':
      return `<span ${outerAttrs}${a11y(node)}>${getIconSvg(node.iconName, '', visualProp(node, roleProps, 'iconSize', 24))}</span>`
    case 'badge':
      return `<span ${outerAttrs}>${node.iconName ? getIconSvg(node.iconName, '', visualProp(node, roleProps, 'iconSize', 14)) : ''}${escapeText(nodeContent(node, projectContent))}</span>`
    case 'rating': {
      // El mismo dibujo que el editor y la página publicada (ver `rating.ts`).
      const stars = ratingIconsHtml({
        value: visualProp(node, roleProps, 'ratingValue', RATING_DEFAULTS.value),
        size: visualProp(node, roleProps, 'ratingSize', RATING_DEFAULTS.size),
        max: node.ratingMax,
        icon: node.ratingIcon,
        empty: node.ratingEmpty,
        showValue: node.ratingShowValue,
      }, (value, max) => `${value} / ${max}`)
      const textCss = roleCss(theme, 'paragraph')
      const content = nodeContent(node, projectContent)
      return `<div ${outerAttrs}>${stars}${content ? `<span${textCss ? ` style="${textCss}"` : ''}>${escapeText(content)}</span>` : ''}</div>`
    }
    case 'avatarGroup': {
      const count = visualProp(node, roleProps, 'avatarCount', 4)
      const urls = node.avatarUrls?.slice(0, count) ?? []
      const size = Math.max(20, Math.min(80, visualProp(node, roleProps, 'avatarSize', 36)))
      const itemStyle = `width:${size}px;height:${size}px;border-radius:999px;border:2px solid #fff;overflow:hidden;display:block;flex-shrink:0;position:relative`
      const avatars = urls.length > 0
        ? urls.map((url, i) => `<span style="${itemStyle};left:${i === 0 ? 0 : -8 * i}px"><img src="${escapeAttr(url)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" /></span>`).join('')
        : Array.from({ length: count }, (_, i) => `<span style="${itemStyle};left:${i === 0 ? 0 : -8 * i}px;background:linear-gradient(135deg,#67e8f9,#0f766e);opacity:${1 - i * 0.08}"></span>`).join('')
      return `<div ${outerAttrs}>${avatars}</div>`
    }
    case 'stat':
      return `<div ${outerAttrs}><strong>${escapeText(nodeContent(node, projectContent))}</strong>${node.accentContent ? `<span style="${roleCss(theme, 'paragraph')};display:block">${escapeText(node.accentContent)}</span>` : ''}</div>`
    case 'image': {
      const src = nodeSrc(node, projectContent)
      const displaySrc = src || IMAGE_PLACEHOLDER_SRC
      return `<img ${outerAttrs} src="${escapeAttr(displaySrc)}" alt="${escapeAttr(node.isDecorative ? '' : node.alt ?? '')}"${node.isDecorative ? ' aria-hidden="true"' : ''} />`
    }
    case 'button':
      if (node.interaction?.action === 'link' || nodeHasHref(node, projectContent)) {
        return `<a ${outerAttrs} href="${escapeAttr(nodeHref(node, projectContent))}"${a11y(node)}>${escapeText(nodeContent(node, projectContent))}</a>`
      }
      return `<button ${outerAttrs}${a11y(node)}>${escapeText(nodeContent(node, projectContent))}</button>`
    case 'link': {
      const inner = nodeContent(node, projectContent) ? escapeText(nodeContent(node, projectContent)) : children
      return `<a ${outerAttrs} href="${escapeAttr(nodeHref(node, projectContent))}"${a11y(node)}>${inner}</a>`
    }
    default: {
      /* El mapa, con la dirección que se escribe en el paso de contacto (o la
         suya, si la tiene). Antes era una caja vacía en la vista previa. */
      if ((node.type as string) === 'embed' && (node as { schemaId?: string }).schemaId === 'google-map') {
        const own = new URLSearchParams(String(node.content ?? '')).get('q')?.trim() ?? ''
        const address = own || (projectContent?.contact?.address ?? '').trim()
        if (address) {
          const src = `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`
          return `<div ${outerAttrs}><iframe src="${escapeAttr(src)}" title="Map" loading="lazy" style="display:block;width:100%;height:100%;min-height:inherit;border:0"></iframe></div>`
        }
      }
      return `<div ${outerAttrs}>${children}</div>`
    }
  }
}

export function elementsToHtml(elements: ElementNode[], breakpoint: Breakpoint, theme?: PageTheme, startIndex = 0, brand?: { id: string; text: string }, projectContent?: ProjectContent): string {
  /* Primero las secciones reutilizables: en el árbol guardado son cáscaras sin
     hijos, así que hay que devolverles el contenido del original ANTES de
     dibujar. Sin esto una cabecera compartida sale como un div vacío. */
  const resolved = resolveComponentInstances(elements, projectContent)
  return resolved.map((node, i) => nodeToHtml(node, breakpoint, theme, startIndex + i, brand, projectContent)).join('\n')
}

function parseFontPx(value: string | undefined): number {
  if (!value) return 0
  const m = /([\d.]+)\s*(px|rem|em)?/.exec(value)
  if (!m) return 0
  const n = parseFloat(m[1]!)
  return m[2] === 'rem' || m[2] === 'em' ? n * 16 : n
}

/** Id of the largest text/heading node — used as the "brand" slot in skeleton mode. */
function findBrandNodeId(elements: ElementNode[], breakpoint: Breakpoint, theme?: PageTheme): string | undefined {
  let bestId: string | undefined
  let bestSize = -1
  const walk = (n: ElementNode) => {
    if (n.type === 'text' || n.type === 'richHeading') {
      const size = parseFontPx(resolveThemedStyles(n, breakpoint, theme).fontSize)
      if (size > bestSize) { bestSize = size; bestId = n.id }
    }
    for (const c of n.children ?? []) walk(c)
  }
  elements.forEach(walk)
  return bestId
}

// ─── page styles ─────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_STYLES: ElementStyles = { desktop: { overflowX: 'hidden', overflowY: 'visible' } }

function pageStylesToCanvasCss(pageStyles: ElementStyles | undefined, breakpoint: Breakpoint): { rootCss: string; contentCss: string } {
  const record = stylesToRecord(resolveStyles(pageStyles ?? DEFAULT_PAGE_STYLES, breakpoint))
  const root = { ...record }
  const content = { ...record }
  delete content.overflow; delete content.overflowX; delete content.overflowY
  if (root.overflowX === 'hidden' && (!root.overflowY || root.overflowY === 'visible' || root.overflowY === 'auto')) {
    root.overflowX = 'clip'; root.overflowY = 'visible'
  }
  return { rootCss: stylesToCss(root), contentCss: stylesToCss(content) }
}

// ─── srcDoc ──────────────────────────────────────────────────────────────────────

const FONTS_HREF = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=Geist:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500;700&family=Lato:wght@300;400;700&family=Lora:wght@400;500;600;700&family=Merriweather:wght@300;400;700&family=Montserrat:wght@300;400;500;600;700;800&family=Nunito:wght@300;400;500;600;700;800&family=Open+Sans:wght@300;400;600;700&family=Oswald:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&family=Poppins:wght@300;400;500;600;700;800&family=Quicksand:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&family=Work+Sans:wght@300;400;500;600;700;800&display=swap"

/** Progressive skeleton: each flag = that aspect is still hidden (shown as placeholder). */
export type SkeletonFlags = { text?: boolean; images?: boolean; colors?: boolean }

function skeletonCss(sk?: SkeletonFlags): string {
  if (!sk || (!sk.text && !sk.images && !sk.colors)) return ''
  const parts: string[] = []
  // Neutralize colors → grayscale tokens so the whole layout reads as a skeleton.
  if (sk.colors) {
    parts.push(`:root{--wb-background:#ffffff;--wb-backgroundSecondary:#f4f5f7;--wb-card:#ffffff;--wb-surface:#eceef1;--wb-border:#e6e8ec;--wb-text:#aeb4bd;--wb-muted:#c8cdd4;--wb-primary:#dde0e5;--wb-accent:#dde0e5;}`)
  }
  // Text/headings/buttons/links → animated gray bars (kept content sizes the bar).
  if (sk.text) {
    parts.push(`#wb-content :where(h1,h2,h3,h4,h5,h6,p,a,button,strong,small,li):not([data-brand]){color:transparent !important;background-image:none !important;background-color:#e6e8ec !important;border-color:transparent !important;box-shadow:none !important;border-radius:7px !important;animation:wb-sk 1.4s ease-in-out infinite;}`)
    // The brand slot (largest heading) shows the company name in serif over the skeleton.
    parts.push(`#wb-content [data-brand]{font-family:'Noto Serif JP',serif !important;font-weight:400 !important;color:#1c1c1e !important;background:none !important;box-shadow:none !important;animation:none !important;-webkit-text-fill-color:#1c1c1e !important;}`)
  }
  if (sk.images) {
    parts.push(`#wb-content img{filter:grayscale(1) contrast(.8) brightness(1.12) !important;opacity:.5 !important;}`)
  }
  parts.push(`@keyframes wb-sk{0%,100%{opacity:1}50%{opacity:.55}}`)
  return parts.join('\n')
}

export function buildPreviewSrcDoc(elements: ElementNode[], theme?: PageTheme, breakpoint: Breakpoint = 'desktop', skeleton?: SkeletonFlags, brandName?: string, projectContent?: ProjectContent): string {
  const brand = skeleton?.text && brandName?.trim()
    ? (() => { const id = findBrandNodeId(elements, breakpoint, theme); return id ? { id, text: brandName.trim() } : undefined })()
    : undefined
  const body = elementsToHtml(elements, breakpoint, theme, 0, brand, projectContent)
  const themeVars = themeVarsToCss(theme)
  const page = pageStylesToCanvasCss(DEFAULT_PAGE_STYLES, breakpoint)
  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="${FONTS_HREF}" rel="stylesheet" />
<style>
  :root { ${themeVars} }
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; min-height: 100vh; overflow-x: clip; overflow-y: visible; ${page.rootCss} }
  /* The iframe is sized to the full page height and scrolled from the outer pane, so it
     should never show its own scrollbar — including the brief window while text reflows
     after a font change and the height re-measures. */
  html { scrollbar-width: none; -ms-overflow-style: none; }
  html::-webkit-scrollbar, body::-webkit-scrollbar { width: 0; height: 0; display: none; }
  #wb-content { min-height: 100vh; display: flex; flex-direction: column; align-items: stretch; ${page.contentCss} }
  /* Reset UA margins on the actual emitted tags (the editor relies on
     [data-element-id]{margin:0}, but the preview doesn't emit that attribute —
     so without this, h1/p keep their default browser margins and the layout
     looks like it has huge vertical gaps). :where() keeps specificity 0 so
     element/role/rich-content styles still win. */
  #wb-content :where(p, h1, h2, h3, h4, h5, h6, ul, ol, figure, blockquote, pre) { margin: 0; }
  a { text-decoration: none; }
  /* Match the editor/published UA resets so buttons/hr don't show default chrome. */
  button { -webkit-appearance: none; appearance: none; border: 0; background: transparent; cursor: pointer; }
  hr { margin: 0; border: none; }
  /* Behavior panels (mobile nav, dropdowns, popovers) are collapsed by default in the
     real render (via the behavior runtime). This static preview has no runtime, so hide
     them by their schema id — otherwise every menu renders expanded and can overflow. */
  [data-wb-schema-id="nav-toggle"],
  [data-wb-schema-id="nav-panel"],
  [data-wb-schema-id="nav-dropdown"],
  [data-wb-schema-id="wb-popover-panel"] { display: none !important; }
  ${richContentCss(theme)}
  ${skeletonCss(skeleton)}
</style></head>
<body><div id="wb-content">${body}</div></body></html>`
}
